import type {
  ICenterInfra,
  IHost,
  ISoftwareInstance,
  ISoftwareType,
} from "@/features/servers/schemas/servers";
import { HOST_KIND_LABELS } from "@/features/servers/schemas/servers";
import { formatInfraAddress } from "@/features/servers/utils/address";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

/**
 * ¿Este usuario tiene ficha de infraestructura?
 *
 * Es un predicado sobre el DATO, no sobre permisos. Antes iba mezclado con la
 * comprobación de capacidad dentro de `canAccessCenterServers`, así que no se
 * podía reutilizar ninguna de las dos mitades por separado. El permiso lo
 * comprueba quien llama, con `can("servers:view")`.
 */
export function isCenterLike(targetUser: User): boolean {
  return targetUser.type === "institution" || targetUser.type === "physician";
}

interface FichaTextInput {
  user: User | null | undefined;
  infra: ICenterInfra;
  hosts: IHost[];
  instances: ISoftwareInstance[];
  softwareTypes: ISoftwareType[];
}

function typeName(
  softwareTypeId: string,
  softwareTypes: ISoftwareType[]
): string {
  const found =
    softwareTypes.find((t) => t.id === softwareTypeId) ||
    softwareTypes.find((t) => t.key === softwareTypeId);
  return found?.displayName ?? softwareTypeId;
}

/**
 * Texto plano de la ficha para copiar / compartir.
 */
export function buildFichaPlainText({
  user,
  infra,
  hosts,
  instances,
  softwareTypes,
}: FichaTextInput): string {
  const lines: string[] = [];
  const name = user ? getUserFullName(user) : infra.institutionUid;
  lines.push(`FICHA DE INFRAESTRUCTURA — ${name}`);
  if (user?.email) lines.push(`Email: ${user.email}`);
  const infraAddr = formatInfraAddress(infra.address);
  if (infraAddr) lines.push(`Dirección (ficha): ${infraAddr}`);
  lines.push("");

  if (infra.contacts.length > 0) {
    lines.push("CONTACTOS IT");
    for (const c of infra.contacts) {
      lines.push(
        `  - ${c.name}${c.role ? ` (${c.role})` : ""}${c.phone ? ` · ${c.phone}` : ""}${c.email ? ` · ${c.email}` : ""}`
      );
    }
    lines.push("");
  }

  if (infra.publicIps.length > 0) {
    lines.push("IPs PÚBLICAS");
    for (const ip of infra.publicIps) lines.push(`  - ${ip}`);
    lines.push("");
  }

  if (hosts.length > 0) {
    lines.push("HOSTS");
    for (const h of hosts) {
      lines.push(`  • ${h.name} [${HOST_KIND_LABELS[h.kind]}]`);
      const bits = [
        h.ip && `IP ${h.ip}`,
        h.hostname && `Host ${h.hostname}`,
        (h.osFamily || h.osVersion || h.os) &&
          `SO ${
            [h.osFamily, h.osVersion].filter(Boolean).join(" ") || h.os
          }`,
        h.anydeskId && `AnyDesk ${h.anydeskId}`,
        h.hardware,
        h.verifiedAt
          ? `Verificado ${h.verifiedAt}${h.verifiedBy ? ` por ${h.verifiedBy}` : ""}`
          : "Sin verificar",
      ].filter(Boolean);
      if (bits.length) lines.push(`    ${bits.join(" · ")}`);
      for (const f of h.customFields) {
        lines.push(`    ${f.key}: ${f.value}`);
      }
    }
    lines.push("");
  }

  if (instances.length > 0) {
    lines.push("SOFTWARE");
    for (const inst of instances) {
      const host = hosts.find((h) => h.id === inst.hostId);
      lines.push(
        `  • ${typeName(inst.softwareTypeId, softwareTypes)}${inst.active ? "" : " (inactivo)"}`
      );
      const bits = [
        inst.version && `versión ${inst.version}`,
        inst.lastUpdatedAt && `update ${inst.lastUpdatedAt}`,
        inst.verifiedAt
          ? `Verificado ${inst.verifiedAt}${inst.verifiedBy ? ` por ${inst.verifiedBy}` : ""}`
          : "Sin verificar",
        host && `Host: ${host.name}`,
        inst.mail,
        inst.url,
      ].filter(Boolean);
      if (bits.length) lines.push(`    ${bits.join(" · ")}`);
      for (const ep of inst.dicomEndpoints) {
        lines.push(
          `    DICOM ${ep.label ? `[${ep.label}] ` : ""}${ep.aetitle}:${ep.port}${ep.ip ? `@${ep.ip}` : ""}`
        );
      }
      for (const f of inst.customFields) {
        lines.push(`    ${f.key}: ${f.value}`);
      }
    }
    lines.push("");
  }

  if (infra.customFields.length > 0) {
    lines.push("CAMPOS CUSTOM (CENTRO)");
    for (const f of infra.customFields) {
      lines.push(`  - ${f.key}: ${f.value}`);
    }
  }

  return lines.join("\n").trim();
}
